import { ChevronDown } from "lucide-react";

import { ABN, CONTACT_EMAIL, LEGAL_NAME } from "@/lib/site";

/**
 * The trading address, also the "To" line of the model withdrawal form in the
 * Terms (CRD Annex I(B) and CCR Sch. 3 Part B want the trader's geographical
 * address there), which takes the name, ABN and email from src/lib/site.ts as
 * this block does. One string, so the two can never name different places.
 */
export const TRADING_ADDRESS = '2 Hibernia Rise, Sorrento WA 6020, Australia';

/**
 * The registered business name, ABN, trading address and contact details.
 *
 * WHY THIS IS A COMPONENT AND NOT A BLOCK INSIDE THE FOOTER. It used to live
 * only in src/components/layout/footer.tsx, and src/lib/public-copy.test.ts
 * pins the published street address by reading that exact path. It was split
 * out when the seller storefront rendered it too, so a second verbatim copy
 * could never drift from the first.
 *
 * One call site today: the global footer. The storefront (/@{username}) stopped
 * rendering it by owner decision on 2026-09-28; its footer is "Powered by
 * supa.place" plus the currency control only. Every other route, including
 * /cart, /checkout and product pages where the contract is formed, still gets
 * the global footer and so this block (storefront-bar.test.ts pins that).
 * public-copy.test.ts reads this file for the address, so keep the one string
 * here rather than inlining it back into the footer.
 *
 * WHY IT IS REQUIRED RATHER THAN DECORATIVE. The trading address is pre-contract
 * information for distance selling to EU and UK consumers (Consumer Rights
 * Directive Art. 6(1)(c)), it is the controller address the Privacy Policy
 * names, and supa.place is Merchant of Record, so it is OUR identity that has
 * to appear beside a price, not the seller's. No seller-facing setting may hide
 * it, which is why it takes no props that could switch it off.
 *
 * A server component: it has no state and no handlers, so it renders into the
 * HTML a crawler and a no-JavaScript visitor receive.
 */
export function BusinessIdentification({ className }: { className?: string }) {
  return (
    <details className={`group ${className ?? "mt-6"}`}>
      <summary className="mx-auto md:mx-0 w-fit flex items-center gap-1 cursor-pointer text-xs text-muted-foreground hover:text-muted-foreground transition-colors list-none [&::-webkit-details-marker]:hidden">
        <span>Business identification</span>
        <ChevronDown aria-hidden className="h-3 w-3 transition-transform group-open:rotate-180" />
      </summary>
      {/* The trading address is required pre-contract information for
          distance selling to EU/UK consumers (Consumer Rights Directive
          Art. 6(1)(c)), and it is the controller address the Privacy Policy
          names. It has to be a real geographical address, not a PO box. */}
      <p className="mt-2 text-center md:text-left text-xs text-muted-foreground">
        {`SUPA PLACE is the registered business name of ${LEGAL_NAME}, ABN ${ABN}.`}
        <br />
        {`${TRADING_ADDRESS}.`}
      </p>
      <p className="mt-1 text-center md:text-left text-xs text-muted-foreground">
        <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-muted-foreground transition-colors">
          {CONTACT_EMAIL}
        </a>
        <span aria-hidden className="mx-1.5">&middot;</span>
        <a href="tel:+61481069946" className="hover:text-muted-foreground transition-colors">
          +61 481 069 946
        </a>
      </p>
    </details>
  );
}
